"use client";

import { useEffect, useState } from "react";

const RING_PATH =
  "M 19 2.5 C 24 3 25.5 5 23 8 C 20 11.5 14 15 7 18 C 3 19 0 17 1.3 14";

const MIN_DISPLAY_MS = 750;
const MAX_DISPLAY_MS = 4000;
const FADE_DURATION_MS = 400;

let _initialLoaderShown = false;

export function resetPageLoader() {
  _initialLoaderShown = false;
}

export interface PageLoaderProps {
  ready?: boolean;
  error?: boolean;
  /** Force the loader visible (e.g. during project switch). */
  forceShow?: boolean;
  /** Show on every mount, even if the session-wide initial loader already completed. */
  alwaysShow?: boolean;
}

export function PageLoader({
  ready = false,
  error = false,
  forceShow = false,
  alwaysShow = false,
}: PageLoaderProps) {
  const [phase, setPhase] = useState<"visible" | "fading" | "gone">(() =>
    _initialLoaderShown && !alwaysShow ? "gone" : "visible",
  );

  // Force-show: immediately go visible when forceShow becomes true
  useEffect(() => {
    if (forceShow && phase === "gone") {
      setPhase("visible");
    }
  }, [forceShow, phase]);

  const canDismiss = forceShow ? false : (ready || error);

  useEffect(() => {
    if (phase !== "visible") return;

    let minDone = false;
    let shouldDismiss = canDismiss;
    let cancelled = false;

    function tryFade() {
      if (cancelled || !minDone || !shouldDismiss) return;
      setPhase("fading");
    }

    const tMin = setTimeout(() => {
      minDone = true;
      tryFade();
    }, MIN_DISPLAY_MS);

    const tMax = setTimeout(() => {
      shouldDismiss = true;
      minDone = true;
      tryFade();
    }, MAX_DISPLAY_MS);

    if (canDismiss) {
      shouldDismiss = true;
      tryFade();
    }

    return () => {
      cancelled = true;
      clearTimeout(tMin);
      clearTimeout(tMax);
    };
  }, [canDismiss, phase]);

  useEffect(() => {
    if (phase !== "fading") return;
    const t = setTimeout(() => {
      _initialLoaderShown = true;
      setPhase("gone");
    }, FADE_DURATION_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === "gone") return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--surface-primary)",
        opacity: phase === "fading" ? 0 : 1,
        transform: phase === "fading" ? "scale(1.04)" : "scale(1)",
        filter: phase === "fading" ? "blur(6px)" : "none",
        transition:
          phase === "fading"
            ? "opacity 350ms cubic-bezier(0.4,0,1,1), transform 400ms cubic-bezier(0.4,0,1,1), filter 350ms cubic-bezier(0.4,0,1,1)"
            : "none",
      }}
      aria-hidden="true"
    >
      <div
        style={{
          position: "absolute",
          width: 320,
          height: 260,
          borderRadius: "9999px",
          filter: "blur(24px)",
          background:
            "radial-gradient(ellipse at center, rgba(255,175,83,0.06) 0%, transparent 70%)",
        }}
      />

      <svg
        viewBox="0 0 26 20"
        style={{ position: "relative", width: 140, height: "auto" }}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <clipPath id="arbi-logo-clip">
            <path d="M21.5865 1.74208C22.6714 1.69492 23.9996 1.78834 24.856 2.53398C25.2684 2.89304 25.4764 3.36863 25.5097 3.90582C25.5257 4.16342 25.4781 4.44511 25.4193 4.69564C25.0906 6.09764 24.0956 7.45183 23.1457 8.52391C22.7732 8.93608 22.3894 9.33754 21.9942 9.7283C21.5991 10.1191 21.1932 10.4985 20.7766 10.8667C20.1625 11.4212 19.5291 11.9531 18.8764 12.4623C18.2237 12.9715 17.5535 13.4567 16.8656 13.918C16.0442 14.4645 15.2035 14.98 14.3435 15.4645C13.7668 15.7953 13.1925 16.1272 12.6006 16.4307C10.0807 17.7223 7.13985 18.7905 4.29097 18.9654C3.14824 19.0356 1.64691 18.868 0.753495 18.0833C0.329485 17.7109 0.0363813 17.1973 0.00461452 16.6286C-0.0966964 14.8147 1.49157 12.6786 2.6639 11.3764C2.86906 11.4184 3.07447 11.4591 3.28013 11.4985C3.19595 11.5871 3.12329 11.6929 3.04853 11.7896C2.83835 12.0616 2.64253 12.3394 2.45486 12.627C2.30283 12.871 2.16344 13.1219 2.03672 13.3798C1.6569 14.151 1.32407 15.1327 1.62327 15.9847C1.79739 16.4806 2.18756 16.8279 2.65489 17.0484C4.59879 17.9656 7.48644 17.1666 9.41895 16.4847C13.3829 15.086 17.513 12.2822 20.4802 9.33851C21.4357 8.39066 22.4282 7.31331 23.0113 6.09163C23.3114 5.46281 23.5393 4.71124 23.2886 4.0273C23.1347 3.6072 22.8323 3.32976 22.4319 3.14288C20.922 2.43805 18.1138 2.99296 16.5531 3.4475C16.2224 3.54384 15.8973 3.66 15.5733 3.77625C15.5045 3.60472 15.4074 3.43699 15.3245 3.27137C15.4987 3.21229 15.6701 3.13119 15.8415 3.06314C16.1263 2.95114 16.4139 2.84647 16.7041 2.74914C16.901 2.68155 17.099 2.6171 17.298 2.5558C17.497 2.4945 17.697 2.43637 17.8978 2.38142C18.0987 2.32648 18.3004 2.27474 18.503 2.2262C18.7055 2.17766 18.9088 2.13235 19.1128 2.09027C19.3168 2.04819 19.5215 2.00936 19.7267 1.97378C19.932 1.93821 20.1377 1.9059 20.344 1.87686C20.5503 1.84782 20.757 1.82207 20.9642 1.7996C21.1713 1.77713 21.3787 1.75796 21.5865 1.74208Z" />
            <path d="M13.9718 0.998569C13.9888 1.01062 13.9919 1.02216 13.9983 1.04119C14.1059 1.36218 14.1836 1.69594 14.2817 2.02025C14.5706 2.97474 14.9259 3.90998 15.4655 4.75499C16.4297 6.26511 17.8875 7.24901 19.5386 7.90016C19.8582 8.02624 20.3059 8.20556 20.6397 8.28006C20.5763 8.41842 19.7558 9.19241 19.5837 9.36433C19.5415 9.40601 19.5006 9.44006 19.4508 9.47254C19.218 9.47932 18.9571 9.38556 18.7376 9.31551C18.6689 9.29324 18.6006 9.27014 18.5326 9.2462C18.4645 9.22227 18.3968 9.1975 18.3293 9.17189C18.2619 9.14628 18.1948 9.11985 18.1281 9.0926C18.0613 9.06535 17.9949 9.03729 17.9288 9.0084C17.8627 8.97954 17.797 8.94985 17.7317 8.91935C17.6663 8.88885 17.6014 8.85756 17.5369 8.82546C17.4723 8.79339 17.4081 8.76052 17.3444 8.72685C17.2807 8.69318 17.2174 8.65874 17.1545 8.62352C17.0916 8.5883 17.0292 8.55232 16.9672 8.51557C16.9052 8.47883 16.8437 8.44131 16.7826 8.40304C16.7216 8.36478 16.661 8.32578 16.6009 8.28604C16.5408 8.2463 16.4812 8.20582 16.4222 8.16459C16.3631 8.12338 16.3045 8.08145 16.2465 8.0388C16.1885 7.99615 16.131 7.95281 16.074 7.90876C16.0171 7.86469 15.9607 7.81994 15.9048 7.77451C15.849 7.72906 15.7937 7.68293 15.739 7.63612C15.6843 7.58932 15.6302 7.54185 15.5767 7.49373C15.5232 7.44559 15.4703 7.39681 15.418 7.3474C15.3657 7.29796 15.314 7.24789 15.263 7.19719C15.2119 7.1465 15.1615 7.0952 15.1117 7.04326C15.062 6.99133 15.0128 6.93879 14.9644 6.88565C14.9159 6.83249 14.8681 6.77876 14.821 6.72446C14.7738 6.67013 14.7274 6.61525 14.6816 6.55981C14.6358 6.50434 14.5908 6.44834 14.5464 6.39179C14.502 6.33523 14.4584 6.27813 14.4154 6.22048C14.3725 6.16284 14.3302 6.1047 14.2887 6.04604C14.2472 5.98737 14.2064 5.9282 14.1664 5.86853C14.1264 5.80884 14.0871 5.74868 14.0485 5.68805C14.01 5.62741 13.9722 5.56631 13.9352 5.50475C13.8982 5.44319 13.8619 5.38119 13.8265 5.31874C13.791 5.2563 13.7563 5.19342 13.7224 5.13011C13.6885 5.06681 13.6553 5.00309 13.623 4.93897C13.4797 4.66116 13.0751 3.84436 13.0546 3.55554C13.0469 3.44591 13.1376 3.27826 13.1801 3.17483C13.4711 2.45913 13.735 1.7337 13.9718 0.998569Z" />
            <path d="M2.77572 9.18016C3.2079 9.31784 3.64457 9.42087 4.08267 9.53696C4.63309 9.6838 5.17983 9.84316 5.72288 10.015C7.92405 10.7261 9.7965 11.9129 11.0872 13.8566C11.3131 14.1968 11.5072 14.553 11.6932 14.9157L11.2103 15.1275C10.8729 15.2855 10.5204 15.425 10.1758 15.5675C10.0333 15.2514 9.83071 14.9462 9.63685 14.6588C8.38277 12.7996 6.4514 11.8091 4.33234 11.2246C4.11377 11.1641 3.8937 11.1097 3.67212 11.0614C3.47864 11.0183 2.90942 10.9287 2.77524 10.8485C2.72112 10.6979 2.76708 10.1228 2.76975 9.93784C2.7734 9.68527 2.77058 9.43273 2.77572 9.18016Z" />
            <path d="M11.9507 17.3959C11.9604 17.4046 11.962 17.4119 11.9615 17.4255C11.9589 17.5118 11.8219 17.8576 11.7879 17.9577C11.7015 18.212 11.619 18.4675 11.5404 18.7243C11.5189 18.797 11.4737 19.0461 11.4175 19.0888C11.3816 19.0298 11.3696 18.9245 11.3525 18.8562C11.2558 18.4982 11.1719 18.1328 11.0604 17.7793C11.3615 17.6666 11.6531 17.5194 11.9507 17.3959Z" />
            <path d="M13.4939 0H13.5119C13.5181 0.138672 13.2152 0.980585 13.1544 1.16657C12.4813 3.22437 11.7591 5.32475 10.263 6.95496C10.2028 7.02072 10.1417 7.08552 10.0795 7.14937C10.0173 7.21321 9.95406 7.27606 9.88987 7.3379C9.82567 7.39976 9.76051 7.46059 9.69438 7.52039C9.62823 7.5802 9.56115 7.63896 9.49313 7.69664C9.42512 7.75432 9.35621 7.81093 9.28639 7.86646C9.21658 7.92199 9.14589 7.9764 9.07432 8.02969C9.00277 8.083 8.93039 8.13516 8.85717 8.18617C8.78395 8.2372 8.70994 8.28707 8.63514 8.33577C8.56034 8.38445 8.48478 8.43194 8.40847 8.47826C8.33215 8.52457 8.2551 8.56967 8.17733 8.61357C8.09958 8.65747 8.02115 8.70013 7.94204 8.74155C7.86292 8.78297 7.78316 8.82315 7.70274 8.86207C7.62235 8.90097 7.54136 8.9386 7.45977 8.97496C7.37816 9.01134 7.296 9.04642 7.21329 9.08019C7.13057 9.11397 7.04734 9.14644 6.96359 9.17761C6.87983 9.20876 6.7956 9.23859 6.71089 9.26711C6.49748 9.33845 6.13506 9.46265 5.91216 9.47066C5.69423 9.47848 4.90411 9.23066 4.63278 9.16055C4.29481 9.07499 3.95548 8.99514 3.61478 8.921C3.37731 8.86685 3.13886 8.80491 2.89875 8.76439C3.11869 8.69706 3.33571 8.61935 3.55692 8.55641C3.97825 8.43651 4.40152 8.33289 4.81942 8.20006C7.22139 7.43664 8.94878 6.43296 10.1657 4.15183C10.6387 3.2652 10.9739 2.29959 11.2731 1.34396C11.3517 1.09552 11.4257 0.845773 11.4953 0.594721C11.5351 0.452801 11.6054 0.117244 11.6699 0.0108726C11.8717 0.0423347 12.1523 0.0191746 12.3612 0.0193436C12.7155 0.0196302 13.0778 0.0366107 13.4308 0.0173434C13.4554 0.0160004 13.4718 0.0099833 13.4939 0Z" />
            <path d="M12.8744 16.9546C12.9888 16.8815 13.1134 16.8231 13.2332 16.7589C13.4277 16.6539 13.6217 16.5479 13.8151 16.4409C14.2784 16.1848 14.7319 15.911 15.1954 15.6556C14.8216 16.3126 14.5436 17.0113 14.286 17.7197C14.1188 18.1746 13.9699 18.6352 13.8393 19.1017C13.7672 19.3536 13.7128 19.6386 13.6203 19.8815C13.5991 19.9372 13.5773 19.9654 13.5282 20H11.8599C12.0216 19.3057 12.2536 18.612 12.4914 17.9397C12.6067 17.6136 12.7218 17.2645 12.8744 16.9546Z" />
            <path d="M11.668 0H13.4939C13.4718 0.0099833 13.4554 0.0160004 13.4308 0.0173434C13.0778 0.0366107 12.7155 0.0196302 12.3612 0.0193436C12.1523 0.0191746 11.8717 0.0423347 11.6699 0.0108726L11.668 0Z" />
          </clipPath>

          <clipPath id="arbi-ring-clip">
            <path d="M21.5865 1.74208C22.6714 1.69492 23.9996 1.78834 24.856 2.53398C25.2684 2.89304 25.4764 3.36863 25.5097 3.90582C25.5257 4.16342 25.4781 4.44511 25.4193 4.69564C25.0906 6.09764 24.0956 7.45183 23.1457 8.52391C22.7732 8.93608 22.3894 9.33754 21.9942 9.7283C21.5991 10.1191 21.1932 10.4985 20.7766 10.8667C20.1625 11.4212 19.5291 11.9531 18.8764 12.4623C18.2237 12.9715 17.5535 13.4567 16.8656 13.918C16.0442 14.4645 15.2035 14.98 14.3435 15.4645C13.7668 15.7953 13.1925 16.1272 12.6006 16.4307C10.0807 17.7223 7.13985 18.7905 4.29097 18.9654C3.14824 19.0356 1.64691 18.868 0.753495 18.0833C0.329485 17.7109 0.0363813 17.1973 0.00461452 16.6286C-0.0966964 14.8147 1.49157 12.6786 2.6639 11.3764C2.86906 11.4184 3.07447 11.4591 3.28013 11.4985C3.19595 11.5871 3.12329 11.6929 3.04853 11.7896C2.83835 12.0616 2.64253 12.3394 2.45486 12.627C2.30283 12.871 2.16344 13.1219 2.03672 13.3798C1.6569 14.151 1.32407 15.1327 1.62327 15.9847C1.79739 16.4806 2.18756 16.8279 2.65489 17.0484C4.59879 17.9656 7.48644 17.1666 9.41895 16.4847C13.3829 15.086 17.513 12.2822 20.4802 9.33851C21.4357 8.39066 22.4282 7.31331 23.0113 6.09163C23.3114 5.46281 23.5393 4.71124 23.2886 4.0273C23.1347 3.6072 22.8323 3.32976 22.4319 3.14288C20.922 2.43805 18.1138 2.99296 16.5531 3.4475C16.2224 3.54384 15.8973 3.66 15.5733 3.77625C15.5045 3.60472 15.4074 3.43699 15.3245 3.27137C15.4987 3.21229 15.6701 3.13119 15.8415 3.06314C16.1263 2.95114 16.4139 2.84647 16.7041 2.74914C16.901 2.68155 17.099 2.6171 17.298 2.5558C17.497 2.4945 17.697 2.43637 17.8978 2.38142C18.0987 2.32648 18.3004 2.27474 18.503 2.2262C18.7055 2.17766 18.9088 2.13235 19.1128 2.09027C19.3168 2.04819 19.5215 2.00936 19.7267 1.97378C19.932 1.93821 20.1377 1.9059 20.344 1.87686C20.5503 1.84782 20.757 1.82207 20.9642 1.7996C21.1713 1.77713 21.3787 1.75796 21.5865 1.74208Z" />
          </clipPath>

          <radialGradient id="gold-orb-gradient">
            <stop offset="0%" stopColor="#FFAF53" stopOpacity="1" />
            <stop offset="20%" stopColor="#FFAF53" stopOpacity="0.8" />
            <stop offset="45%" stopColor="#FFAF53" stopOpacity="0.35" />
            <stop offset="70%" stopColor="#FFAF53" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#FFAF53" stopOpacity="0" />
          </radialGradient>

          <filter id="light-bloom" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="1.2" />
          </filter>
        </defs>

        <g clipPath="url(#arbi-logo-clip)">
          <rect width="26" height="20" fill="var(--border-alpha)" />
        </g>

        <g clipPath="url(#arbi-ring-clip)">
          <circle r="5" fill="url(#gold-orb-gradient)">
            <animateMotion
              dur="0.75s"
              repeatCount="1"
              calcMode="paced"
              path={RING_PATH}
              fill="remove"
            />
          </circle>

          <circle
            r="6"
            fill="url(#gold-orb-gradient)"
            filter="url(#light-bloom)"
            opacity="0.5"
          >
            <animateMotion
              dur="0.75s"
              repeatCount="1"
              calcMode="paced"
              path={RING_PATH}
              fill="remove"
            />
          </circle>
        </g>
      </svg>
    </div>
  );
}
